<template>
  <div>
    <div class="promo-header-container">
      <div class="flex-col">
        <div class="promo-header mb-1">Offers</div>
        <div class="sac__font-14 n70 align-center">
          <button type="button" @click="showInfo" class="mr-2">
            <span class="icon-luc-CircleHelp sac__font-18 f-primary" /></button
          >Create, manage and monitor promotional offers for your members here.
        </div>
      </div>
      <button type="button" class="sac__button bg-primary" @click="toggleInfoModal(true)">
        <span class="icon-luc-Plus sac__font-18 f-white mr-2" />
        Add new offer
      </button>
    </div>
    <div id="promo-table-container">
      <div v-if="promos.length > 0" class="promo-body-container">
        <CategoryTab
          :loading="loading"
          :leftActiveTab="activeTab"
          :leftCategory="[
            {
              value: 'active',
              label: 'Active',
              count: activeCount,
            },
            {
              value: 'inactive',
              label: 'Inactive',
              count: inactiveCount,
            },
          ]"
          :leftToggle="toggleProfileTab"
        />
        <v-data-table
          :headers="getHeaders()"
          :items="tableData"
          :loading="loading"
          fixed-header
          hide-default-footer
        >
          <template #[`item.image`]="{ item }">
            <td style="width: 270px">
              <img
                :src="item.backgroundUri"
                style="width: 200px; border-radius: 4px; margin-top: 4px"
              />
            </td>
          </template>
          <template #[`item.status`]="{ item }">
            <td></td>
          </template>
          <template #[`item.plans`]="{ item }">
            <td style="max-width: 110px">
              <div v-if="item?.plans?.length > 0" class="flex flex-column">
                {{ plans.find((x) => x.planId === item.plans?.[0])?.name }} ({{
                  plans.find((x) => x.planId === item.plans?.[0])?.planType === 'Hardware'
                    ? 'Detect'
                    : 'Connect'
                }})
              </div>
              <span v-if="item?.plans?.length > 1">
                +{{ item?.plans.length - 1 }} more plans
                <button
                  @click="
                    () => {
                      selectedPlans = item?.plans;
                      showPlansPopup = true;
                    }
                  "
                >
                  <span class="icon-luc-ChevronDown f-primary sac__font-16" />
                </button>
              </span>
            </td>
          </template>
          <template #[`item.runtime`]="{ item }">
            <td>
              <div class="flex flex-column">
                <div class="flex-row">
                  <span class="sac__font-18 n70 icon-luc-CalendarCheck mr-2" />
                  {{ item.startDate }}
                </div>
                <div class="flex-row">
                  <span class="sac__font-18 n70 icon-luc-CalendarX mr-2" />
                  {{ item.active ? '--' : item.endDate }}
                </div>
              </div>
            </td>
          </template>
          <!-- Delivered header -->
          <template #header.delivered>
            <div class="header-with-icon">
              <button @click="showAdditionalInfo">
                <span class="icon-luc-Info mr-2" />
              </button>
              Delivered
            </div>
          </template>
          <template #[`item.delivered`]="{ item }">
            <td>
              <b>{{ item.delivered }}</b
              >/{{ getTotalMembers(item) }}
            </td>
          </template>
          <template #[`item.activateSwitch`]="{ item }">
            <td>
              <SACToggleSwitch
                :disabled="item.status === 'Draft' || checkStatus(item) === 'Expired'"
                :checked="item.status === 'Created' && checkStatus(item) !== 'Expired'"
                :change="
                  () => {
                    toggleStatusPopup(
                      item.status === 'Created' ? 'showArchivePopup' : 'showRestorePopup',
                      true,
                      item,
                    );
                  }
                "
              />
            </td>
            <td class="pl-2">
              {{ checkStatus(item) }}
            </td>
          </template>
          <template #[`item.edit`]="{ item }">
            <td>
              <button type="button" @click="editPromo(item)">
                <span class="sac__font-18 f-primary icon-luc-PenLine"></span>
              </button>
            </td>
          </template>
        </v-data-table>
      </div>
      <div v-else-if="loading" class="loading-container">
        <img src="@shared/assets/img/general/loading.svg" />
      </div>
      <div v-else class="promo-body-container">
        <div>No active promos to show</div>
        <div class="promo-note">
          Create special offers for your customers with our promo wizard. You&apos;ll be guided
          through steps to design a promotion with your own images and eligibility criteria.
        </div>
      </div>
      <ActionModal :currentCorporation="currentCorporation" :partnerSettings="partnerSettings" />
      <ConfirmArchivePopup
        :show="showArchivePopup"
        :close="() => (showArchivePopup = false)"
        :archivePromo="archivePromo"
      />
      <ConfirmRestorePopup
        :show="showRestorePopup"
        :close="() => (showRestorePopup = false)"
        :restorePromo="restorePromo"
      />
      <InfoModal
        :show="showInfoModal"
        :view="infoView"
        :close="() => (showInfoModal = false)"
        :openBuilder="showNewPromo ? handleInfoClose : null"
      />
      <OfferPlansModal
        :show="showPlansPopup"
        view="info"
        :close="() => (showPlansPopup = false)"
        :plans="selectedPlans"
        :allPlans="plans"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

import promoMixin from '@shared/mixins/promoMixin';
import CategoryTab from '@shared/components/shared/CategoryTab.vue';
import ActionModal from './ActionModal.vue';
import InfoModal from './InfoModal.vue';
import SACToggleSwitch from '../sac/SACToggleSwitch.vue';
import ConfirmArchivePopup from '@shared/components/promo/modal/ConfirmArchivePopup.vue';
import ConfirmRestorePopup from '@shared/components/promo/modal/ConfirmRestorePopup.vue';
import OfferPlansModal from './OfferPlansModal.vue';

export default {
  name: 'PromoTable',
  components: {
    ActionModal,
    InfoModal,
    CategoryTab,
    SACToggleSwitch,
    ConfirmArchivePopup,
    ConfirmRestorePopup,
    OfferPlansModal,
  },
  mixins: [promoMixin],
  props: {
    currentCorporation: Object,
    partnerSettings: Object,
  },
  data() {
    return {
      loading: false,
      showInfoModal: false,
      infoView: '',
      showNewPromo: false,
      showArchivePopup: false,
      showPlansPopup: false,
      selectedPlans: [],
      showRestorePopup: false,
      modalPromo: {},
    };
  },
  computed: {
    tableData() {
      return this.promoTableData;
    },
    ...mapState({
      plans: (state) => state.providerPlan.plans,
    }),
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch('getPromos', this.currentCorporation.id);
    await this.$store.dispatch('getProviderPlans', {
      corpId: this.currentCorporation.id,
    });
    this.loading = false;
  },
  methods: {
    getTotalMembers(promo) {
      if (!promo?.plans || !this.plans?.length) {
        return 0;
      }

      return promo.plans
        .map((e) => this.plans.find((x) => x.planId === e)?.activeMembers ?? 0)
        .reduce((a, b) => a + b, 0);
    },
    showInfo() {
      this.showNewPromo = false;
      this.showInfoModal = true;
      this.infoView = 'promo-info';
    },
    showAdditionalInfo(e) {
      e.stopPropagation();
      this.showInfoModal = true;
      this.infoView = 'promo-additional-info';
    },
    toggleInfoModal(showNewPromo) {
      this.showNewPromo = showNewPromo;
      if (this.promos.length === 0) {
        this.showInfoModal = true;
        this.infoView = 'promo-info';
      } else {
        this.toggleActionModal(true, 'new-promo');
      }
    },
    handleInfoClose() {
      this.showInfoModal = false;
      this.toggleActionModal(true, 'new-promo');
    },
    editPromo(promo) {
      this.$store.dispatch('changePromoValue', {
        field: 'selectedPromo',
        value: promo,
      });
      this.toggleActionModal(true, 'edit-promo');
    },
    checkStatus(promo) {
      const { startDate, endDate, status } = promo;
      const promoStatus = status;
      const currentDate = moment();
      if (status === 'Created') {
        if (currentDate.isBefore(startDate)) {
          return 'Scheduled';
        }
        if (currentDate.isAfter(startDate) && currentDate.isBefore(endDate)) {
          return 'Active';
        }
        if (currentDate.isAfter(endDate)) {
          return 'Expired';
        }
      }
      return promoStatus;
    },
    getHeaders() {
      return [
        {
          text: 'Thumbnail',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'image',
        },
        {
          text: 'Name',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'promoName',
        },
        {
          text: 'Eligible plans',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'plans',
        },
        {
          text: 'Runtime',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'runtime',
        },
        {
          text: 'Delivered',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'delivered',
        },
        {
          text: 'Viewed',
          align: 'center',
          sortable: true,
          filterable: true,
          value: 'views',
        },
        {
          text: 'Clicks',
          align: 'center',
          sortable: true,
          filterable: true,
          value: 'clicks',
        },
        {
          text: 'Status',
          align: 'center',
          sortable: true,
          filterable: true,
          value: 'activateSwitch',
        },
        {
          text: 'Edit',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'edit',
        },
      ];
    },
    toggleStatusPopup(field, show, action) {
      this[field] = show;
      this.modalPromo = action;
    },
    async archivePromo() {
      try {
        this.loading = true;
        await this.$store.dispatch('updatePromo', {
          promo: {
            ...this.modalPromo,
            status: 'Archived',
          },
        });
        this.showArchivePopup = false;
        this.$store.dispatch('showSuccessToast', {
          message: 'Success! Your promo was archived.',
        });
      } catch (err) {
        this.showArchivePopup = false;
        this.$store.dispatch('showAlertToast', {
          message:
            'There was an unexpected error while archiving this promo. Please try again or contact support',
        });
      }
      this.loading = false;
    },
    async restorePromo() {
      try {
        this.loading = true;
        await this.$store.dispatch('updatePromo', {
          promo: {
            ...this.modalPromo,
            status: 'Created',
          },
        });
        this.showRestorePopup = false;
        this.$store.dispatch('showSuccessToast', {
          message: 'Success! Your promo was restored.',
        });
      } catch (err) {
        this.showRestorePopup = false;
        this.$store.dispatch('showAlertToast', {
          message:
            'There was an unexpected error while restoring this promo. Please try again or contact support',
        });
      }
      this.loading = false;
    },
  },
};
</script>
<style lang="scss">
.promo-header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 18px;
  .promo-header {
    display: flex;
    align-items: center;
    font-size: 1.5625rem;
    color: $N100;
    font-weight: 600;
    font-size: 18px;
  }
  .new-promo-btn {
    width: 120px;
    height: 40px;
    background-color: $NBLUE100;
    text-align: center;
    color: $WHITE;
    font-weight: 600;
    font-size: 15px;
    line-height: 15px;
    border-radius: 8px;
  }
}
.ac-info-btn {
  width: 24px;
  height: 24px;
  background-color: $N90;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  box-shadow: 0px 4px 9px rgba(0, 0, 0, 0.12);
  margin-bottom: 4px;
}
#promo-table-container {
  border: 1px solid $N10;
  padding: 20px;
  border-radius: 8px;

  .promo-body-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;
    .v-data-table__wrapper {
      thead {
        tr {
          th {
            background-color: $NAVY100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;
            text-transform: uppercase;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }
            &:first-child {
              border-top-left-radius: 6px;
            }
            &:last-child {
              border-top-right-radius: 6px;
            }
            &.sortable {
              i {
                display: none;
              }
            }
            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }
      tbody {
        tr {
          td {
            border-bottom: 0;
            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }
            .edit-btn {
              background: $N90;
              color: $WHITE;
              width: 70px;
              padding: 3px;
              border-radius: 6px;
            }
            .pending {
              background: $SUNSET100;
              padding: 2px 10px;
              border-radius: 12px;
              color: $WHITE;
              font-weight: 700;
            }
            .cancelled {
              background: $TOMATO100;
              padding: 2px 10px;
              border-radius: 12px;
              color: $WHITE;
              font-weight: 700;
            }
            .confirmed {
              background: $BASIL100;
              padding: 2px 10px;
              border-radius: 12px;
              color: $WHITE;
              font-weight: 700;
            }
            .p-service {
              font-weight: 700;
            }
          }
          &:hover {
            background: none;
          }
          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
    .promo-note {
      color: $NBLUE100;
      font-size: 0.75em;
      font-weight: 400;
    }
  }
  .grey-border {
    border: 1px solid $N10;
    padding: 10px;
    border-radius: 8px;
  }
  .edit-promo-txt {
    font-size: 1em;
    font-weight: 600;
    text-align: center;
    text-decoration: underline;
  }
}
</style>
